import { Document, Edit, NewTab, View, ViewOff } from '@carbon/react/icons';
import { Button, InlineNotification } from '@carbon/react';
import { EditDocumentModal } from 'components/edit-document-modal';
import { PageHeader } from 'components/page-header';
import { useReducer, type ReactNode } from 'react';
import { Tabs } from './tabs';
import type { DocumentResponse } from 'types/api-responses';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { TitleWorkspaceReferrer } from 'components/title-workspace-referrer';
import { DocumentAttributes } from 'components/document-attributes';
import * as ProvisionedDocument from 'components/provisioned-document';
import {
  ActivityFeedPanel,
  ActivityFeedPanelOpenButton,
} from 'components/activity-feed-panel';
import { useAtom, useSetAtom } from 'jotai';
import { documentLayoutAtom } from 'pages/documents/structuring/atoms/document-layout';
import {
  AgentSurface,
  AgentTriggerButton,
  DOCUMENT_AGENT,
  useAgentDockWidth,
} from 'components/agent-chat';
import {
  documentProvisionStore,
  showProvisionedDocumentAtom,
  refreshDocumentCounter,
} from './atoms';

import { DOCUMENT_PAGE_CONTENT_CLASS } from '../utils';
import style from './header.module.scss';
import { documentRootCrumb } from './utils';
import { EntityCrumbLabel } from '../entity-crumb-label';

/**
 * The document page's header actions: the agent, the map-and-details toggle, the
 * document image, edit and the activity feed.
 * @param {object} props The actions.
 * @param {DocumentResponse} props.document The document.
 * @param {Function} props.getDocument Refetches the document after an edit.
 * @return {JSX.Element} The actions.
 */
function ActionButtons({
  document,
  getDocument,
}: {
  document: DocumentResponse;
  getDocument: () => Promise<void>;
}) {
  const [openEdit, setOpenEdit] = useReducer((s) => !s, false);
  const setShowProvisionedDocument = useSetAtom(showProvisionedDocumentAtom);
  const refreshDocument = useSetAtom(refreshDocumentCounter);
  const [layout, setLayout] = useAtom(documentLayoutAtom);

  if (!document) {
    return null;
  }

  // AFE and Division Order Doc Structuring both expose a document-level
  // "Comments & Activity" flyout (shared ActivityFeedPanel), opened from the
  // document header so it's reachable from every structuring section. The feed
  // is the shared base-class Query::ActivityFeeds keyed on the Document (so
  // modelType is just document.type); only the comments-create endpoint differs
  // per structuring type. Non-structuring document types get no entry here.
  const structuringCommentsHrefByType: Record<string, string> = {
    'Document::Afe': `/documents/${document.id}/afe_structuring/comments`,
    'Document::DivisionOrder': `/documents/${document.id}/division_order_structuring/comments`,
  };
  const structuringCommentsHref = document.type
    ? structuringCommentsHrefByType[document.type]
    : undefined;

  const imageHref = document.resources.image?.href;
  const completionsHref = document.resources.completions.href;
  const configurationHref = document.resources.completionsConfiguration.href;

  return (
    <>
      {document.resources.agentMessages?.href && (
        <AgentTriggerButton agent={DOCUMENT_AGENT} />
      )}
      <Button
        kind="tertiary"
        size="sm"
        renderIcon={layout.showMapAndDetails ? ViewOff : View}
        onClick={() =>
          setLayout({
            ...layout,
            showMapAndDetails: !layout.showMapAndDetails,
          })
        }
      >
        <span>
          {layout.showMapAndDetails
            ? 'Hide Map & Details'
            : 'Show Map & Details'}
        </span>
      </Button>
      {imageHref && (
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={NewTab}
          href={imageHref}
          target="_blank"
        >
          <span>View Document Image</span>
        </Button>
      )}
      <ProvisionedDocument.AvailableProvisions
        href={completionsHref}
        configurationHref={configurationHref}
        onProvisionsAvailable={() => {
          setShowProvisionedDocument(true);
          refreshDocument();
        }}
        store={documentProvisionStore}
        hasImage={!!imageHref}
      />
      {document.duplicate && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Duplicate Document"
          subtitle="This document has been marked as duplicate and should not be used. Please use the valid copy of this document."
        />
      )}
      <Button
        kind="primary"
        size="sm"
        renderIcon={Edit}
        onClick={() => {
          setOpenEdit();
        }}
      >
        <span>Edit Document</span>
      </Button>
      {structuringCommentsHref && (
        <ActivityFeedPanelOpenButton buttonSize="sm" tooltipPosition="bottom" />
      )}
      <EditDocumentModal
        document={document}
        isOpen={openEdit}
        onSave={() => getDocument()}
        onClose={setOpenEdit}
      />
      {structuringCommentsHref && (
        <ActivityFeedPanel
          modelType={document.type ?? ''}
          modelId={Number(document.id)}
          title="Comments & Activity"
          subtitle={document.documentTitle}
          commentsHref={structuringCommentsHref}
        />
      )}
    </>
  );
}

/**
 * Props for a document tab's content. The document is fetched once by
 * DocumentEntrypointPage and handed down, so switching tabs swaps only the
 * panel beneath a persistent header instead of refetching and remounting.
 */
export type DocumentPageProps = {
  document: DocumentResponse;
  getDocument: () => Promise<void>;
  children?: ReactNode;
};

/**
 * The document page's header: breadcrumbs back to where the document was
 * opened, its title, attributes, actions and tabs, and the document agent.
 * @param {DocumentPageProps} props The document and the tab content beneath.
 * @return {JSX.Element} The header and its content.
 */
function DocumentHeader({
  children,
  document,
  getDocument,
}: DocumentPageProps) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const referrer = params.get('referrer');
  const dataset = params.get('dataset');

  const crumb = documentRootCrumb(referrer, dataset);
  const rootCrumb: [string, ReactNode] = [
    crumb.href,
    crumb.entity ? (
      <EntityCrumbLabel
        key="root-crumb"
        href={crumb.href}
        fallback={crumb.label}
      />
    ) : (
      crumb.label
    ),
  ];

  const agentResource = document.resources.agentMessages;
  const agentHref = agentResource?.href;
  // 0 while the dock is closed, so the padding below is a no-op then.
  const agentWidth = useAgentDockWidth(DOCUMENT_AGENT);
  const agentSubtitle = `Ask about ${
    document.documentTitle || 'this document'
  }`;

  return (
    <>
      {/* The dock is position: fixed, so pad the page content out from under it.
          The wrapper is also a named container, so the header's action buttons
          collapse to icons off the width left over rather than the viewport. */}
      <div
        className={classNames(
          style.contentWrapper,
          DOCUMENT_PAGE_CONTENT_CLASS
        )}
        style={agentWidth ? { paddingRight: agentWidth } : undefined}
      >
        <PageHeader
          breadcrumbs={[rootCrumb, ['', document.documentTitle]]}
          title={document.documentTitle}
          actions={
            <ActionButtons document={document} getDocument={getDocument} />
          }
          iconComponent={<Document size={24} />}
          attributes={<DocumentAttributes document={document} />}
          referrer={
            referrer ? <TitleWorkspaceReferrer referrer={referrer} /> : null
          }
          stickyHeader
        >
          <Tabs document={document} />
        </PageHeader>
        <div>{children}</div>
      </div>
      {agentHref && (
        <AgentSurface
          agent={DOCUMENT_AGENT}
          agentMessagesHref={agentHref}
          subject={{ type: 'Document', id: document.id }}
          subtitle={agentSubtitle}
          model={agentResource?.model}
        />
      )}
    </>
  );
}

export { DocumentHeader };
